/**
 * Shopper cashback wallet — iteration 40.
 *
 * Reads from GET /api/wallet/cashback and renders the three-tier summary
 * (PENDING · AVAILABLE · REDEEMED) plus a paginated list of the shopper's
 * cashback rows. This is the consumer-facing complement to the merchant
 * cashback view built in iter 39.
 */
import { useEffect, useState } from "react";
import { Wallet, Clock, CheckCircle, Undo2 } from "lucide-react";
import api from "@/lib/api";

const STATUS_LABEL = {
  PENDING:   { icon: Clock,       tint: "bg-amber-50 text-amber-800 border-amber-200",     label: "Pending — awaiting refund window" },
  APPROVED:  { icon: CheckCircle, tint: "bg-cyan-50 text-cyan-800 border-cyan-200",         label: "Approved — will be available soon" },
  AVAILABLE: { icon: Wallet,      tint: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Available to redeem" },
  REDEEMED:  { icon: CheckCircle, tint: "bg-slate-100 text-slate-700 border-slate-200",     label: "Redeemed" },
  CANCELLED: { icon: Undo2,       tint: "bg-slate-100 text-slate-500 border-slate-200",     label: "Cancelled" },
  REVERSED:  { icon: Undo2,       tint: "bg-rose-50 text-rose-700 border-rose-200",         label: "Reversed (refund)" },
};

function fmtCents(minor, currency = "CAD") {
  const dollars = (minor || 0) / 100;
  return dollars.toLocaleString("en-CA", { style: "currency", currency });
}

export default function ShopperCashback() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/wallet/cashback")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load cashback"));
  }, []);

  if (err) {
    return <div className="max-w-3xl mx-auto px-6 py-12 text-sm text-rose-700" data-testid="cashback-error">{err}</div>;
  }
  if (!data) {
    return <div className="max-w-3xl mx-auto px-6 py-12 text-sm text-slate-500" data-testid="cashback-loading">Loading your cashback…</div>;
  }

  const totals = data.totals_minor || {};
  const items = data.items || [];

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="cashback-page">
      <header className="mb-8">
        <div className="pf-chip pf-chip-cyan w-fit"><Wallet className="w-3 h-3" /> Your wallet</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight text-slate-900 mt-3">Cashback</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-xl leading-relaxed">
          Every verified Cyannect purchase earns a percentage back. Cashback stays in <span className="font-medium text-amber-800">Pending</span> during the refund window, then moves to <span className="font-medium text-emerald-800">Available</span>. Refunded orders return their cashback automatically.
        </p>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <SummaryTile label="Pending"   minor={totals.PENDING || 0}   accent="text-amber-800"   testid="cashback-tile-pending" />
        <SummaryTile label="Available" minor={totals.AVAILABLE || 0} accent="text-emerald-700" testid="cashback-tile-available" />
        <SummaryTile label="Redeemed"  minor={totals.REDEEMED || 0}  accent="text-slate-700"   testid="cashback-tile-redeemed" />
      </div>

      {/* Redeem CTA — only enabled once shopper hits the $20 threshold. */}
      {(totals.AVAILABLE || 0) >= 2000 ? (
        <div className="pf-card p-5 mt-4 flex items-center justify-between gap-3 flex-wrap bg-gradient-to-r from-cyan-50 to-emerald-50 border border-emerald-200" data-testid="cashback-redeem-ready">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-800 font-semibold">Ready to redeem</div>
            <div className="text-sm text-slate-900 mt-1">Convert your available cashback into a digital gift card.</div>
          </div>
          <a href="/wallet/redeem" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold" data-testid="cashback-redeem-btn">
            Redeem rewards →
          </a>
        </div>
      ) : (
        <div className="pf-card p-4 mt-4" data-testid="cashback-below-threshold">
          <div className="text-xs text-slate-500">
            You need <span className="font-semibold text-slate-900">${((2000 - (totals.AVAILABLE || 0)) / 100).toFixed(2)}</span> more available cashback to unlock gift-card redemption. The minimum is $20 CAD.
          </div>
          <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all"
                 style={{ width: `${Math.min(100, ((totals.AVAILABLE || 0) / 2000) * 100).toFixed(1)}%` }} />
          </div>
        </div>
      )}

      <section className="mt-8">
        <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Recent activity</h2>
        {items.length === 0 ? (
          <div className="pf-card p-6 text-sm text-slate-500 text-center" data-testid="cashback-empty">
            No cashback yet. Redeem a Cyannect offer at any merchant to start earning.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((r) => {
              const meta = STATUS_LABEL[r.status] || STATUS_LABEL.PENDING;
              const Icon = meta.icon;
              return (
                <div key={r.id} className="pf-card p-4 flex items-center gap-4"
                     data-testid={`cashback-row-${r.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${meta.tint}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      Order {r.order_id || "—"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {meta.label} · {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-display text-lg tabular-nums ${r.status === "REVERSED" || r.status === "CANCELLED" ? "text-slate-400 line-through" : "text-slate-900"}`}>
                      {fmtCents(r.cashback_minor, r.currency)}
                    </div>
                    <div className="text-[10px] text-slate-500">{(r.rate_pct || 1).toFixed(2)}% back</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryTile({ label, minor, accent, testid }) {
  return (
    <div className="pf-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-2xl md:text-3xl font-medium tabular-nums mt-1 ${accent}`}>
        {fmtCents(minor)}
      </div>
    </div>
  );
}
